import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import axios, { type CancelTokenSource } from "axios";
import { getError } from "../../utils/getError";
import { useVehiclesSocketStore } from "./vehiclesSocketStore";
import { isPlatformAdminUser } from "../../hooks/useIsReseller";
import type { Trip } from "./tripsStore";

interface CompletedTripFlag {
  completedAt: number;
  seen: boolean;
}

interface ActiveTripsStore {
  trips: Trip[];
  byVehicleId: Record<string, Trip>;
  byRemolqueId: Record<string, Trip>;
  byDeviceId: Record<string, Trip>;
  completedTrips: Record<string, CompletedTripFlag>;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  cancelToken: CancelTokenSource | null;
  fetchActiveTrips: (force?: boolean) => Promise<void>;
  upsertTrip: (trip: Trip) => void;
  removeTripById: (tripId: string) => void;
  markTripCompleted: (tripId: string, deviceId?: string) => void;
  markCompletedTripsSeen: () => void;
  reset: () => void;
}

const CACHE_TTL_MS = 60 * 1000; // 60 seconds
const COMPLETED_RETENTION_MS = 60 * 60 * 1000;

function canFetchActiveTrips(): boolean {
  const isReseller =
    isPlatformAdminUser();
  if (!isReseller) return true;
  return Boolean(useVehiclesSocketStore.getState().selectedCompany);
}

function hasActiveAssignment(trip: Trip): boolean {
  if (!trip.assignments || !Array.isArray(trip.assignments)) return false;
  return trip.assignments.some(
    (a) => a.status === "active" || a.status === "pending",
  );
}

function buildIndex(trips: Trip[]): {
  byVehicleId: Record<string, Trip>;
  byRemolqueId: Record<string, Trip>;
  byDeviceId: Record<string, Trip>;
} {
  const byVehicleId: Record<string, Trip> = {};
  const byRemolqueId: Record<string, Trip> = {};
  const byDeviceId: Record<string, Trip> = {};
  for (const trip of trips) {
    if (trip.vehicle_id) byVehicleId[trip.vehicle_id] = trip;
    if (trip.remolque_id) byRemolqueId[trip.remolque_id] = trip;
    for (const a of trip.assignments || []) {
      if (a.device_id && (a.status === "active" || a.status === "pending")) {
        byDeviceId[a.device_id] = trip;
      }
    }
  }
  return { byVehicleId, byRemolqueId, byDeviceId };
}

export const useActiveTripsStore = create<ActiveTripsStore>((set, get) => ({
  trips: [],
  byVehicleId: {},
  byRemolqueId: {},
  byDeviceId: {},
  completedTrips: {},
  loading: false,
  error: null,
  lastFetchedAt: null,
  cancelToken: null,

  fetchActiveTrips: async (force = false) => {
    if (!canFetchActiveTrips()) {
      const { trips, cancelToken: pendingToken } = get();
      if (trips.length > 0 || pendingToken) get().reset();
      return;
    }
    const { loading, lastFetchedAt, cancelToken } = get();
    if (loading) return;
    if (
      !force &&
      lastFetchedAt &&
      Date.now() - lastFetchedAt < CACHE_TTL_MS
    ) {
      return;
    }
    if (cancelToken) cancelToken.cancel("new active trips request");

    const newCancelToken = axios.CancelToken.source();
    set({ loading: true, error: null, cancelToken: newCancelToken });

    try {
      const { data } = await api.get("/tripsList", {
        params: { status: "selected", limit: 500, page: 1 },
        cancelToken: newCancelToken.token,
      });
      const allTrips: Trip[] = data?.data || [];
      const activeTrips = allTrips.filter(hasActiveAssignment);

      const { trips: prevTrips, completedTrips } = get();
      const serverIds = new Set(activeTrips.map((t) => t.id));
      const now = Date.now();
      const keptCompleted = prevTrips.filter((t) => {
        if (serverIds.has(t.id)) return false;
        const flag = completedTrips[t.id];
        return (
          !!flag && !flag.seen && now - flag.completedAt < COMPLETED_RETENTION_MS
        );
      });
      const nextTrips = [...keptCompleted, ...activeTrips];
      const nextCompleted: Record<string, CompletedTripFlag> = {};
      for (const t of keptCompleted) nextCompleted[t.id] = completedTrips[t.id];

      set({
        trips: nextTrips,
        ...buildIndex(nextTrips),
        completedTrips: nextCompleted,
        loading: false,
        lastFetchedAt: Date.now(),
        cancelToken: null,
      });
    } catch (err: unknown) {
      if (axios.isCancel(err) || isAbortError(err)) {
        set({ loading: false, cancelToken: null });
        return;
      }
      set({
        error: getError(err),
        loading: false,
        cancelToken: null,
      });
    }
  },

  upsertTrip: (trip: Trip) => {
    set((state) => {
      const filtered = state.trips.filter((t) => t.id !== trip.id);
      const next = hasActiveAssignment(trip) ? [...filtered, trip] : filtered;
      return { trips: next, ...buildIndex(next) };
    });
  },

  removeTripById: (tripId: string) => {
    set((state) => {
      const next = state.trips.filter((t) => t.id !== tripId);
      return { trips: next, ...buildIndex(next) };
    });
  },

  markTripCompleted: (tripId: string, deviceId?: string) => {
    set((state) => {
      if (!state.trips.some((t) => t.id === tripId)) return state;
      const trips = state.trips.map((t) =>
        t.id !== tripId
          ? t
          : {
              ...t,
              assignments: (t.assignments || []).map((a) =>
                deviceId &&
                a.device_id === deviceId &&
                (a.status === "active" || a.status === "pending")
                  ? { ...a, status: "completed" as const }
                  : a,
              ),
            },
      );
      return {
        trips,
        ...buildIndex(trips),
        completedTrips: state.completedTrips[tripId]
          ? state.completedTrips
          : {
              ...state.completedTrips,
              [tripId]: { completedAt: Date.now(), seen: false },
            },
      };
    });
  },

  markCompletedTripsSeen: () => {
    set((state) => {
      const unseen = Object.keys(state.completedTrips).filter(
        (id) => !state.completedTrips[id].seen,
      );
      if (unseen.length === 0) return state;
      const next = { ...state.completedTrips };
      for (const id of unseen) next[id] = { ...next[id], seen: true };
      return { completedTrips: next };
    });
  },

  reset: () => {
    const { cancelToken } = get();
    if (cancelToken) cancelToken.cancel("reset");
    set({
      trips: [],
      byVehicleId: {},
      byRemolqueId: {},
      byDeviceId: {},
      completedTrips: {},
      loading: false,
      error: null,
      lastFetchedAt: null,
      cancelToken: null,
    });
  },
}));

/**
 * Returns the active trip for a vehicle, remolque, or device, if any.
 * Prefers matches by vehicle_id, then remolque_id, then device_id.
 */
export function getActiveTripForUnit(
  vehicleId: string | undefined | null,
  remolqueId: string | undefined | null,
  deviceId: string | undefined | null,
  state: Pick<ActiveTripsStore, "byVehicleId" | "byRemolqueId" | "byDeviceId">,
): Trip | null {
  if (vehicleId && state.byVehicleId[vehicleId]) {
    return state.byVehicleId[vehicleId];
  }
  if (remolqueId && state.byRemolqueId[remolqueId]) {
    return state.byRemolqueId[remolqueId];
  }
  if (deviceId && state.byDeviceId[deviceId]) {
    return state.byDeviceId[deviceId];
  }
  return null;
}
